/**
 * "Connect events" — a channel's system-event subscription (TRUS-684, TRUS-685).
 *
 * Opened from the thread header's channel settings menu (group channels only,
 * never DMs). Everyone who can read the channel sees what it is connected to.
 *
 * Who can edit (the server enforces the same rules: 403 cannot_subscribe):
 *  - platform types (agents / code / deploys): a platform admin in the operator
 *    org — the server says so per type with `can_subscribe`;
 *  - org types (signatures, tasks, workflows, member tasks): an org admin, or
 *    the channel's owner/admin who is staff in the org.
 * The form offers only the types the viewer may subscribe; if the channel is
 * already connected to a type the viewer cannot manage, it stays read-only.
 *
 * PHI (contract B): types marked `phi` only go to PRIVATE channels. In a
 * public or announcement channel they are disabled with a tooltip saying why;
 * the server rejects them too (400 phi_requires_private_channel).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Send, Zap } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useOptionalAuth } from '@/contexts/AuthContext';
import RoleContext from '@/contexts/RoleContext';
import { INTERNAL_SYSTEM_ROLES } from '@/utils/superAdminPolicy';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  EMPTY_FILTERS,
  SystemEventsApiError,
  systemEventsApi,
  type EventSubscription,
  type SystemEventDeliveryMode,
  type SystemEventFilters,
  type SystemEventType,
} from '@/services/systemEventsApi';
import { useIsOrgAdmin } from '../channelActions';
import {
  filterKindsFor,
  groupTypes,
  repoLabel,
  systemEventTypesKey,
  toggleValue,
  typeLabel,
  type TFn,
} from './eventCatalog';
import { EventTypeChecklist } from './EventTypeChecklist';
import { EventFilterFields, describeFilterIds, useEventFilterOptions } from './EventFilterFields';

/** True for a platform admin (super_admin / ops_admin). Safe without providers. */
export function useIsPlatformAdmin(): boolean {
  const role = React.useContext(RoleContext);
  const auth = useOptionalAuth();
  const user = auth?.user as any;
  const roles = [role?.currentRole, user?.role, user?.system_role].filter(Boolean).map(String);
  return (
    roles.some((r) => (INTERNAL_SYSTEM_ROLES as readonly string[]).includes(r)) ||
    user?.is_super_admin === true ||
    user?.is_platform_admin === true
  );
}

/**
 * May this viewer edit the subscription? (Pure, for tests.)
 *
 * Needs at least one type the server lets them subscribe, a role that may
 * manage the channel's events (channel owner/admin, org admin or platform
 * admin), and every type ALREADY connected to be one they may subscribe —
 * otherwise a save would drop or 403 on a type someone else connected.
 */
export function canEditSubscription(
  types: SystemEventType[],
  isChannelManager: boolean,
  isPlatformAdmin: boolean,
  isOrgAdmin = false,
  connectedTypes: string[] = [],
): boolean {
  const subscribable = new Set(types.filter((t) => t.can_subscribe).map((t) => t.type));
  if (subscribable.size === 0) return false;
  if (!(isChannelManager || isPlatformAdmin || isOrgAdmin)) return false;
  return connectedTypes.every((type) => subscribable.has(type));
}

/** True when PHI types may not be posted to this channel (contract B). */
export function isPublicChannelType(channelType: string | null | undefined): boolean {
  return channelType !== 'private';
}

/** A user-facing sentence for a failed save / test. */
export function systemEventsErrorMessage(err: unknown, t: TFn): string {
  const e = err as SystemEventsApiError | undefined;
  switch (e?.code) {
    case 'cannot_subscribe':
      return t('messaging.systemEvents.errors.cannotSubscribe', 'You do not have permission to connect this channel to these events.');
    case 'unknown_event_type':
      return t('messaging.systemEvents.errors.unknownType', 'One of these events is no longer available. Reopen this panel and try again.');
    case 'dm_not_allowed':
      return t('messaging.systemEvents.errors.dmNotAllowed', 'Direct messages cannot be connected to events.');
    case 'phi_requires_private_channel':
      return t(
        'messaging.systemEvents.errors.phiRequiresPrivate',
        'Events about members can only be posted to a private channel. Make this channel private, or turn those events off.',
      );
    case 'invalid_delivery':
      return t('messaging.systemEvents.errors.invalidDelivery', 'Pick how events are delivered: Immediate or Hourly digest.');
    case 'invalid_filter':
      return t(
        'messaging.systemEvents.errors.invalidFilter',
        'One of the programs or people in the filters is not in this organization any more. Check the filters and try again.',
      );
    default:
      break;
  }
  if (e?.status === 404) {
    return t('messaging.systemEvents.errors.notFound', 'This channel no longer exists, or you no longer have access to it.');
  }
  if (e?.status === 403) {
    return t('messaging.systemEvents.errors.cannotSubscribe', 'You do not have permission to connect this channel to these events.');
  }
  return t('messaging.systemEvents.errors.generic', 'Something went wrong. Please try again.');
}

export const eventSubscriptionKey = (channelId: number) => ['channel-event-subscription', channelId] as const;
export { systemEventTypesKey };

interface ChannelEventsDialogProps {
  channelId: number;
  channelName?: string | null;
  /** public / private / announcement — PHI types need 'private'. */
  channelType?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The viewer is this channel's owner or admin. */
  isChannelManager: boolean;
}

export function ChannelEventsDialog({
  channelId,
  channelName,
  channelType,
  open,
  onOpenChange,
  isChannelManager,
}: ChannelEventsDialogProps) {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const isPlatformAdmin = useIsPlatformAdmin();
  const isOrgAdmin = useIsOrgAdmin();
  const publicChannel = isPublicChannelType(channelType);

  const typesQuery = useQuery({
    queryKey: systemEventTypesKey,
    queryFn: () => systemEventsApi.listTypes(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const subQuery = useQuery({
    queryKey: eventSubscriptionKey(channelId),
    queryFn: () => systemEventsApi.getSubscription(channelId),
    enabled: open,
    retry: false,
  });

  const types = typesQuery.data ?? [];
  const subscription = subQuery.data ?? null;
  // Tolerate a partial object (an older cache entry, a test double).
  const subFilters: SystemEventFilters = { ...EMPTY_FILTERS, ...(subscription?.filters ?? {}) };
  const subMode: SystemEventDeliveryMode = subscription?.delivery?.mode === 'hourly_digest' ? 'hourly_digest' : 'immediate';
  const editable = canEditSubscription(
    types,
    isChannelManager,
    isPlatformAdmin,
    isOrgAdmin,
    subscription?.event_types ?? [],
  );

  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<SystemEventFilters>(EMPTY_FILTERS);
  const [active, setActive] = useState(true);
  const [mode, setMode] = useState<SystemEventDeliveryMode>('immediate');

  // Load the saved subscription into the form whenever it (re)arrives.
  useEffect(() => {
    if (!open) return;
    setSelected(subscription?.event_types ?? []);
    setFilters({ ...EMPTY_FILTERS, ...(subscription?.filters ?? {}) });
    setActive(subscription ? subscription.is_active : true);
    setMode(subscription?.delivery?.mode === 'hourly_digest' ? 'hourly_digest' : 'immediate');
  }, [open, subscription]);

  const afterChange = (next: EventSubscription | null) => {
    queryClient.setQueryData(eventSubscriptionKey(channelId), next);
    queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
  };

  // Only the filters that apply to a checked type are sent; the others are
  // cleared, so a stale program filter cannot silently narrow a deploy feed.
  const offered = useMemo(
    () => types.filter((x) => x.can_subscribe || selected.includes(x.type)),
    [types, selected],
  );
  const selectedTypes = useMemo(() => types.filter((x) => selected.includes(x.type)), [types, selected]);
  const activeKinds = useMemo(() => filterKindsFor(selectedTypes), [selectedTypes]);
  const offeredKinds = useMemo(() => filterKindsFor(offered), [offered]);

  const save = useMutation({
    mutationFn: () =>
      systemEventsApi.saveSubscription(channelId, {
        event_types: selected,
        filters: {
          repos: activeKinds.has('repos') ? filters.repos : [],
          services: activeKinds.has('services') ? filters.services : [],
          programs: activeKinds.has('programs') ? filters.programs : [],
          assignees: activeKinds.has('assignees') ? filters.assignees.filter((a) => a !== 'me') : [],
        },
        is_active: active,
        delivery: { mode },
      }),
    onSuccess: (next) => {
      afterChange(next);
      toast.success(
        selected.length === 0
          ? t('messaging.systemEvents.disconnected', 'Channel disconnected from events')
          : t('messaging.systemEvents.saved', 'Event settings saved'),
      );
    },
    onError: (err) => {
      toast.error(t('messaging.systemEvents.saveFailed', 'Could not save event settings'), {
        description: systemEventsErrorMessage(err, t),
      });
    },
  });

  const sendTest = useMutation({
    mutationFn: () => systemEventsApi.sendTest(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-messages', channelId] });
      toast.success(t('messaging.systemEvents.testSent', 'Test message sent to this channel'));
    },
    onError: (err) => {
      toast.error(t('messaging.systemEvents.testFailed', 'Could not send a test message'), {
        description: systemEventsErrorMessage(err, t),
      });
    },
  });

  const groups = useMemo(() => groupTypes(offered), [offered]);
  const loading = typesQuery.isLoading || subQuery.isLoading;
  const busy = save.isPending || sendTest.isPending;

  const phiLockReason = (type: SystemEventType) =>
    type.phi && publicChannel
      ? t(
          'messaging.systemEvents.phiPublicLocked',
          'Events about members can only go to private channels, so member details never reach people outside the care team. Make this channel private to turn this on.',
        )
      : null;

  const readOnlyLabels = (subscription?.event_types ?? []).map((type) => typeLabel(type, types, t));
  const { programs: programOptions, staff: staffOptions } = useEventFilterOptions(
    open && !editable && subFilters.programs.length > 0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="channel-events-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" aria-hidden="true" />
            {t('messaging.systemEvents.title', 'Connect events')}
          </DialogTitle>
          <DialogDescription>
            {channelName
              ? t('messaging.systemEvents.descriptionNamed', 'WeaveIn posts the events you pick into #{{name}}.', { name: channelName })
              : t('messaging.systemEvents.description', 'WeaveIn posts the events you pick into this channel.')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" data-testid="channel-events-loading">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('messaging.systemEvents.loading', 'Loading…')}
          </div>
        ) : subQuery.isError ? (
          <div className="py-4 text-sm text-destructive" role="alert" data-testid="channel-events-load-error">
            {systemEventsErrorMessage(subQuery.error, t)}
          </div>
        ) : !editable ? (
          <div className="space-y-3 py-2" data-testid="channel-events-readonly">
            {readOnlyLabels.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('messaging.systemEvents.none', 'This channel is not connected to any events.')}
              </p>
            ) : (
              <>
                <p className="text-sm">
                  {subscription?.is_active
                    ? t('messaging.systemEvents.readOnlyOn', 'This channel receives these events:')
                    : t('messaging.systemEvents.readOnlyOff', 'This channel is connected to these events, but paused:')}
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="channel-events-readonly-list">
                  {readOnlyLabels.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
                {subFilters.programs.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('messaging.systemEvents.programsOnly', 'Programs: {{list}}', {
                      list: describeFilterIds(subFilters.programs, programOptions, t),
                    })}
                  </p>
                )}
                {subFilters.assignees.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('messaging.systemEvents.assigneesOnly', 'Assigned to: {{list}}', {
                      list: describeFilterIds(subFilters.assignees, staffOptions, t),
                    })}
                  </p>
                )}
                {subFilters.repos.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('messaging.systemEvents.reposOnly', 'Repositories: {{list}}', {
                      list: subFilters.repos.map((r) => repoLabel(r, t)).join(', '),
                    })}
                  </p>
                )}
                {subFilters.services.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('messaging.systemEvents.servicesOnly', 'Services: {{list}}', {
                      list: subFilters.services.join(', '),
                    })}
                  </p>
                )}
                {subMode === 'hourly_digest' && (
                  <p className="text-xs text-muted-foreground" data-testid="channel-events-readonly-digest">
                    {t('messaging.systemEvents.deliveryDigestOnly', 'Delivered as an hourly digest.')}
                  </p>
                )}
              </>
            )}
            <p className="text-xs text-muted-foreground">
              {t(
                'messaging.systemEvents.readOnlyHint',
                'Channel owners and admins, org admins and platform admins can change which events a channel receives.',
              )}
            </p>
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto py-1" data-testid="channel-events-form">
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
              <label htmlFor={`channel-events-active-${channelId}`} className="text-sm font-medium">
                {active
                  ? t('messaging.systemEvents.on', 'On — posting events')
                  : t('messaging.systemEvents.off', 'Off — paused')}
              </label>
              <Switch
                id={`channel-events-active-${channelId}`}
                checked={active}
                onCheckedChange={(v: boolean) => setActive(!!v)}
                aria-label={t('messaging.systemEvents.activeLabel', 'Post events to this channel')}
                disabled={busy}
              />
            </div>

            <EventTypeChecklist
              groups={groups}
              allTypes={types}
              selected={selected}
              onToggle={(type, on) => setSelected((s) => toggleValue(s, type, on))}
              disabled={busy}
              lockReason={phiLockReason}
              phiNote={t(
                'messaging.systemEvents.phiNoteChannel',
                'Only for private channels. Posts never include member details.',
              )}
            />

            <EventFilterFields
              kinds={offeredKinds}
              value={filters}
              onChange={setFilters}
              disabled={busy}
              idPrefix={`channel-events-${channelId}`}
            />

            <fieldset className="space-y-1.5" data-testid="channel-events-delivery">
              <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('messaging.systemEvents.deliveryLegend', 'Delivery')}
              </legend>
              {(
                [
                  [
                    'immediate',
                    t('messaging.systemEvents.deliveryImmediate', 'Immediate'),
                    t(
                      'messaging.systemEvents.deliveryImmediateHint',
                      'Post each event as it happens. A burst of more than 10 in 5 minutes is rolled into one summary.',
                    ),
                  ],
                  [
                    'hourly_digest',
                    t('messaging.systemEvents.deliveryDigest', 'Hourly digest'),
                    t('messaging.systemEvents.deliveryDigestHint', 'One post an hour with the counts and up to 10 titles.'),
                  ],
                ] as const
              ).map(([value, label, hint]) => (
                <label key={value} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name={`channel-events-delivery-${channelId}`}
                    className="mt-0.5 h-4 w-4 accent-[var(--primary,#2563EB)]"
                    checked={mode === value}
                    onChange={() => setMode(value)}
                    disabled={busy}
                    data-testid={`event-delivery-${value}`}
                  />
                  <span>
                    <span className="font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {editable && !loading && !subQuery.isError && (
            <Button
              type="button"
              variant="outline"
              onClick={() => sendTest.mutate()}
              disabled={busy || !subscription}
              title={
                subscription
                  ? undefined
                  : t('messaging.systemEvents.testNeedsSave', 'Save the settings first, then send a test.')
              }
              data-testid="channel-events-test"
            >
              {sendTest.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {t('messaging.systemEvents.sendTest', 'Send test')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('messaging.channel.close', 'Close')}
          </Button>
          {editable && !loading && !subQuery.isError && (
            <Button type="button" onClick={() => save.mutate()} disabled={busy} data-testid="channel-events-save">
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('messaging.channel.save', 'Save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChannelEventsDialog;
